/**
 * The persistent glossary drawer (§7.1), plus the `<Gloss>` inline link that every technical term
 * in the UI uses.
 */

import { useSession } from '../../store/session';
import { TERMS, findTerm } from './terms';

export function Gloss({ id, children }: { id: string; children: React.ReactNode }): JSX.Element {
  const setGlossary = useSession((s) => s.setGlossary);
  const term = findTerm(id);
  return (
    <button
      onClick={() => setGlossary(id)}
      title={term ? `${term.term} — ${term.short}` : undefined}
      style={{
        background: 'none',
        border: 'none',
        borderBottom: '1px dotted var(--ink-dim)',
        padding: 0,
        cursor: 'help',
        color: 'inherit',
        font: 'inherit',
        borderRadius: 0,
      }}
    >
      {children}
    </button>
  );
}

export function GlossaryDrawer(): JSX.Element | null {
  const open = useSession((s) => s.settings.showGlossary);
  const term = useSession((s) => s.glossaryTerm);
  const setGlossary = useSession((s) => s.setGlossary);
  const updateSettings = useSession((s) => s.updateSettings);

  if (!open && !term) return null;
  const active = term ? findTerm(term) : undefined;

  return (
    <aside
      aria-label="Glossary"
      className="scroll"
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        width: 340,
        background: 'var(--surface-1)',
        borderLeft: '1px solid var(--line)',
        padding: 'var(--s4)',
        zIndex: 40,
        boxShadow: '-12px 0 32px rgba(0,0,0,0.4)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h2 className="title" style={{ fontSize: 16, margin: 0 }}>
          Glossary
        </h2>
        <button
          onClick={() => {
            setGlossary(null);
            updateSettings({ showGlossary: false });
          }}
          aria-label="Close glossary"
        >
          ✕
        </button>
      </div>

      {active && (
        <div className="panel" style={{ marginBottom: 16, borderColor: 'var(--ink-dim)' }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{active.term}</div>
          <div style={{ color: 'var(--ink-dim)', marginBottom: 8 }}>{active.short}</div>
          <div>{active.body}</div>
        </div>
      )}

      <div style={{ display: 'grid', gap: 10 }}>
        {TERMS.map((t) => (
          <button
            key={t.id}
            onClick={() => setGlossary(t.id)}
            style={{
              background: 'none',
              border: 'none',
              borderLeft: `2px solid ${t.id === term ? 'var(--water-shallow)' : 'var(--line)'}`,
              padding: '2px 0 2px 10px',
              textAlign: 'left',
              borderRadius: 0,
            }}
          >
            <div style={{ fontWeight: 600 }}>{t.term}</div>
            <div style={{ color: 'var(--ink-dim)', fontSize: 12 }}>{t.short}</div>
          </button>
        ))}
      </div>
    </aside>
  );
}
